import React, { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Appointment, TaskListItem } from '@myapp/contract'
import {
  useDispositionDraftStore,
  useDispositionConfig,
  useGhostCollision,
  useGhostBlock,
  useTimelineInteractions,
  DispositionInteractionProvider,
} from '../../../features/disposition'
import { BreakEditProvider, useBreakEdit } from '../../../features/break-edit'
import { TaskRescheduleProvider, useTaskReschedule } from '../../../features/task-reschedule'
import { AppointmentEditProvider, useAppointmentEditContext, useAppointmentEdit } from '../../../features/appointment-edit'
import { useAppointmentInteractionContext } from '../../../features/appointment-block-interaction'
import { useTimelineDayComposer } from '../model/useTimelineDayComposer'
import { TimelineConfigProvider } from '../model/TimelineConfigContext'
import { useScrollToHour, TIMELINE_WIDTH_PERCENT, TIMELINE_TECH_NAME_WIDTH } from '../../../shared/lib/timeline/useScrollToHour'
import { isWorkingDay, filterSchedulableAppointments, filterSchedulableTimeManagement } from '../../../shared/lib/timeline/weekHelpers'
import { getHourFromIso, hasAllDayTMEntry } from '../../../shared/lib/timeline/dateHelpers'
import { computeLanes, buildRowIntervals, getMaxLaneCount } from '../../../shared/lib/timeline/computeLanes'
import { routes } from '../../../shared/config/routes'
import { useCanRescheduleCompleted } from '../../../shared/hooks/useUserRoles'
import { TimelineRowDataProvider } from '../model/TimelineRowDataContext'
import { TimelineHeader } from './TimelineHeader'
import { TechnicianRow } from './TechnicianRow'
import { TechnicianRowSkeleton } from './TechnicianRowSkeleton'
import { CurrentTimeIndicator } from './CurrentTimeIndicator'
import styles from './Timeline.module.css'


interface Props {
  readonly?: boolean
  onTaskSelect?: (taskId: number) => void
  editingTaskId?: number
}

export const TimeLineWidget = ({
  readonly: isReadonly = false,
  onTaskSelect,
  editingTaskId,
}: Props) => {
  const navigate = useNavigate()
  const canRescheduleCompleted = useCanRescheduleCompleted()

  const { appointmentId, onEditingAppointmentLoaded } = useAppointmentEditContext()
  const { onAppointmentSelect } = useAppointmentInteractionContext()
  const { workOrderId, defaultDurations, ghostColor, onDraftFinalized } = useDispositionConfig()

  const {
    dayIso,
    range,
    data,
    isLoading,
    isFetching,
    breakBlocks,
    getTasksForTechnician,
    timedTasksByTechnician,
    hasCollision: rawHasCollision,
    appointmentWorkOrderMap,
  } = useTimelineDayComposer()

  const {
    ghost,
    setGhost,
    commitGhost,
    clearGhost,
    draftDurations,
    setDraftDurations,
    totalDraftDuration,
    resizeSnapshotRef,
    resizeHandleRef,
  } = useGhostBlock({ defaultDurations, dayIso })

  const handleHydrate = useCallback(
    (appt: Appointment) => {
      const travelDuration = appt.actualTravelDuration ?? appt.plannedTravelDuration
      const workStart = new Date(appt.actualStart ?? appt.plannedStart)
      const anfahrtStartIso = new Date(workStart.getTime() - travelDuration * 60_000).toISOString()
      setDraftDurations({
        plannedTravelDuration: appt.actualTravelDuration ?? appt.plannedTravelDuration,
        plannedWorkDuration: appt.actualWorkDuration ?? appt.plannedWorkDuration,
        plannedDepartureDuration: appt.actualDepartureDuration ?? appt.plannedDepartureDuration,
      })
      setGhost({ technicianId: String(appt.assignedTechnicianId), anfahrtStartIso, committed: true })
    },
    [setDraftDurations, setGhost],
  )

  const { editingAppointment } = useAppointmentEdit({
    appointmentId,
    technicians: data?.technicians,
    onHydrate: handleHydrate,
    onEditingAppointmentLoaded,
  })

  const { getBreakDragStart, getResolvedBreak, draggingBreakInfo } = useBreakEdit({
    technicians: data?.technicians,
    breakBlocks,
    dayIso,
    hasCollision: rawHasCollision,
  })

  const hasCollision = useMemo(
    () =>
      editingTaskId != null
        ? (iso: string, dur: Parameters<typeof rawHasCollision>[1], techId: string, excludeId?: string) =>
            rawHasCollision(iso, dur, techId, excludeId, editingTaskId)
        : rawHasCollision,
    [rawHasCollision, editingTaskId],
  )

  const { taskDragState, startTaskDrag } = useTaskReschedule({
    dayIso,
    hasCollision: rawHasCollision,
    technicians: data?.technicians,
  })

  const interactions = useTimelineInteractions({
    ghost,
    setGhost,
    commitGhost,
    clearGhost,
    draftDurations,
    setDraftDurations,
    totalDraftDuration,
    resizeSnapshotRef,
    resizeHandleRef,
    editingAppointment,
    workOrderId,
    appointmentId,
    technicians: data?.technicians,
    breakBlocks,
    hasCollision,
    onDraftFinalized,
    dayIso,
  })

  const handleTaskClick = useCallback(
    (taskId: number) => {
      if (isReadonly) {
        navigate(routes.aufgaben.edit(taskId))
        return
      }
      if (onTaskSelect) {
        onTaskSelect(taskId)
        return
      }
      const d = new Date(dayIso)
      const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      navigate(`${routes.aufgaben.plan}?taskId=${taskId}&day=${dayKey}`)
    },
    [navigate, dayIso, isReadonly, onTaskSelect],
  )

  const readonlyAppointmentClick = useCallback(
    (apptId: string) => {
      const wId = appointmentWorkOrderMap.get(apptId)
      if (wId) navigate(`${routes.task.detail(wId)}?appointmentId=${apptId}`)
    },
    [appointmentWorkOrderMap, navigate],
  )
  const onAppointmentClick = isReadonly ? readonlyAppointmentClick : onAppointmentSelect

  const isFullDay = useDispositionDraftStore((s) => s.isFullDay)
  const setGhostInvalid = useDispositionDraftStore((s) => s.setGhostInvalid)

  const checkGhostCollision = useGhostCollision({ ghost, draftDurations, hasCollision, appointmentId, isFullDay })

  // Compute invalidity for the ghost's row once (reused in the map below)
  const ghostInvalid = useMemo(() => {
    if (isReadonly || !ghost || !data?.technicians) return false
    const ghostTech = data.technicians.find((t) => t.technicianId === ghost.technicianId)
    if (!ghostTech) return false
    const working = isWorkingDay(ghostTech.workingDays, dayIso)
    const shiftStartHour = ghostTech.shiftStart ? getHourFromIso(ghostTech.shiftStart) : null
    const shiftEndHour = ghostTech.shiftEnd ? getHourFromIso(ghostTech.shiftEnd) : null
    return checkGhostCollision(working, shiftStartHour, shiftEndHour)
  }, [isReadonly, ghost, data?.technicians, dayIso, checkGhostCollision])

  useEffect(() => {
    if (isReadonly) return
    setGhostInvalid(ghostInvalid)
  }, [isReadonly, ghostInvalid, setGhostInvalid])

  const { scrollRef } = useScrollToHour()

  const SKELETON_COUNT = 5
  const widthStyle = `calc(${TIMELINE_WIDTH_PERCENT}% - ${TIMELINE_TECH_NAME_WIDTH * (TIMELINE_WIDTH_PERCENT / 100 - 1)}px)`

  return (
    <div
      ref={scrollRef}
      className={`w-full flex flex-col gap-px overflow-x-auto ${styles.scrollContainer}`}
    >
      <div className="relative" style={{ width: widthStyle }}>
        <TimelineHeader />

        {isLoading && Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <TechnicianRowSkeleton key={i} index={i} />
        ))}
        {isFetching && !isLoading && (
          <div className={styles.fetchingOverlay} />
        )}
        {!isLoading && data?.technicians.length === 0 && (
          <div className="flex items-center justify-center py-16 text-sm text-gray-400">
            Keine Handwerker gefunden
          </div>
        )}

        <DispositionInteractionProvider value={{
          getDraftStart: interactions.getDraftStart,
          getDraftMove: interactions.getDraftMove,
          getDraftCommit: interactions.getDraftCommit,
          getDraftCancel: interactions.getDraftCancel,
          onResizeStart: interactions.handleResizeStart,
          onResizeMove: interactions.handleResizeMove,
          onResizeEnd: interactions.handleResizeEnd,
          onBlockMove: interactions.handleBlockMove,
          onBlockMoveEnd: interactions.handleBlockMoveEnd,
          onBlockMoveToRow: interactions.handleBlockMoveToRow,
        }}>
        <BreakEditProvider value={{ getBreakDragStart }}>
        <TaskRescheduleProvider value={{
          taskDragState: onTaskSelect ? null : taskDragState,
          startTaskDrag: onTaskSelect
            ? (_task: TaskListItem, _techId: string, _e: React.MouseEvent) => {}
            : (task: TaskListItem, techId: string, e: React.MouseEvent) => startTaskDrag(task, techId, e),
        }}>
        <TimelineConfigProvider value={{
          isReadonly,
          editingTaskId,
          onTaskClick: handleTaskClick,
          onAppointmentClick,
          canRescheduleCompleted,
        }}>
          {data?.technicians.map((item, index) => {
            const working = isWorkingDay(item.workingDays, dayIso)
            const shiftStartHour = item.shiftStart ? getHourFromIso(item.shiftStart) : null
            const shiftEndHour = item.shiftEnd ? getHourFromIso(item.shiftEnd) : null
            const schedulableAppointments = filterSchedulableAppointments(item.appointments)
            const schedulableTM = filterSchedulableTimeManagement(item.timeManagementEntries ?? [])
            const { timedTasks, dayLevelTasks } = getTasksForTechnician(item.technicianId)
            const suppressBreak = hasAllDayTMEntry(item.timeManagementEntries ?? [], dayIso)

            const isGhostRow = !isReadonly && ghost?.technicianId === item.technicianId

            const resolvedBreak =
              suppressBreak || (isFullDay && isGhostRow)
                ? undefined
                : isReadonly
                  ? breakBlocks.get(item.technicianId)
                  : getResolvedBreak(item.technicianId)

            const laneMap = computeLanes(
              buildRowIntervals(schedulableAppointments, timedTasks, {
                timeManagementEntries: schedulableTM,
                breakBlock: resolvedBreak ?? null,
                dayIso: range.from,
                shiftStartHour,
                shiftEndHour,
              }),
            )
            const maxLaneCount = getMaxLaneCount(laneMap)

            const ghostBlock = isGhostRow && ghost
              ? {
                  anfahrtStartIso: ghost.anfahrtStartIso,
                  travelDuration: draftDurations.plannedTravelDuration,
                  workDuration: draftDurations.plannedWorkDuration,
                  departureDuration: draftDurations.plannedDepartureDuration,
                  committed: ghost.committed,
                  isColliding: ghostInvalid,
                }
              : null

            return (
              <div key={item.technicianId} className={styles.rowEnter} style={{ animationDelay: `${index * 40}ms` }}>
                <TimelineRowDataProvider
                  value={{
                    technicianId: item.technicianId,
                    technicianName: item.technicianName,
                    appointments: schedulableAppointments,
                    timeManagementEntries: schedulableTM,
                    breakBlock: resolvedBreak,
                    originalBreakBlock: !isReadonly && draggingBreakInfo?.technicianId === item.technicianId ? breakBlocks.get(item.technicianId) : undefined,
                    ghostBlock,
                    isWorkingDay: working,
                    shiftStartHour: shiftStartHour ?? undefined,
                    shiftEndHour: shiftEndHour ?? undefined,
                    timedTasks,
                    dayIso: range.from,
                    laneMap,
                    maxLaneCount,
                  }}
                >
                  <TechnicianRow
                    item={item}
                    dayLevelTasks={dayLevelTasks}
                  />
                </TimelineRowDataProvider>
              </div>
            )
          })}
        </TimelineConfigProvider>
        </TaskRescheduleProvider>
        </BreakEditProvider>
        </DispositionInteractionProvider>

        <CurrentTimeIndicator dayIso={dayIso} />
      </div>
    </div>
  )
}
